import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import { BsArrowRepeat, BsBookmarkPlus, BsChevronDown, BsChevronRight, BsPlus } from 'react-icons/bs';
import { GoFileDirectory } from 'react-icons/go';
import Skeleton from 'react-loading-skeleton';
import AppContext from 'Utils/AppContext';
import { READ_ONLY_TITLE } from 'Auth/permissions';
import RunProfileModal from './RunProfileModal';
import AddToSchemaLibraryDialog from './AddToSchemaLibraryDialog';
import ProfileHistoryTimeline from './ProfileHistoryTimeline';
import SalesforceObjectsView from './SalesforceNetwork';
import { normalizeSnapshot, columnsForTable } from './snapshotUtils';
import { getDataSourceSummary, getResults } from './metaAnalysisApi';
import { listProfileJobs, startProfileJob } from './profileApi';
import { ensureConnectionTested } from 'Utils/connectionGate';
import { getDataSourceById, toUiDataSource } from '../DataSources/datasourcesApi';
import AdoptedFromBadge from '../DataSources/AdoptedFromBadge';
import { formatBytes, displaySniffType } from 'Utils/fileTypeUtils';
import { getSourceNomenclature } from 'Utils/nomenclature';
import FileTypeChip from '../DataSources/file/FileTypeChip';
import { parentDir, baseName, encodeFolderPathForUrl } from './folderPathUtils';
import { BRAND, INK, STATUS, ELEVATION, RADIUS } from 'Utils/styleTokens';
import Breadcrumbs from 'Utils/Breadcrumbs';

const ACTIVE_STATUSES = new Set(['PENDING', 'RUNNING']);
const POLL_INTERVAL_MS = 5000;
// Cap the profile-history fan-out: we fetch one getResults per successful run to plot
// the size timeline (the job-list DTO carries no size), so bound it to the most recent
// runs. 12 is plenty for a manually re-profiled source and keeps the fan-out cheap.
const HISTORY_CAP = 12;

// Small pill buttons for the Expand all / Collapse all controls, matching the discover
// view's folder-panel button styling.
const groupBtnStyle = {
  cursor: 'pointer',
  border: `1px solid ${INK[200]}`,
  borderRadius: 8,
  background: '#fff',
  color: INK[500],
  fontSize: 12,
  padding: '4px 10px',
};

// Compact inline search input used by both the file list and the tables list.
const searchInputStyle = {
  border: `1px solid ${INK[200]}`,
  borderRadius: 8,
  fontSize: 12,
  padding: '4px 10px',
  color: INK[900],
  outline: 'none',
  minWidth: 200,
};

function bytesPretty(bytes) {
  if (bytes == null) return '—';
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return '—';
  if (n >= 1024 * 1024 * 1024) return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${n} B`;
}

function rowsPretty(n) {
  if (n == null) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}K`;
  return String(v);
}

function formatInstant(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toISOString().replace('T', ' ').slice(0, 19) + 'Z';
}


const FactRow = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: 12 }}>
    <span style={{ color: INK[400], fontWeight: 600, minWidth: 130, textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: 10 }}>
      {label}
    </span>
    <span style={{ color: INK[900] }}>{value || '—'}</span>
  </div>
);

// A single big statistic rendered as label + value — used across the top of the
// file-folder profile view (total size, files, analyzed, skipped).
const Stat = ({ label, value, accent }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    <span style={{ fontSize: 22, fontWeight: 500, color: accent || INK[900] }}>{value}</span>
    <span style={{ fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: INK[400] }}>
      {label}
    </span>
  </div>
);

const DataSourceDetail = ({ isAdmin }) => {
  const { dataSourceId } = useParams();
  const history = useHistory();
  const [summary, setSummary] = useState(null);
  // The saved data source record (name / rootPath) — the snapshot's connection block
  // often lacks both for file sources, so the header reads from this instead.
  const [source, setSource] = useState(null);
  const [latestJob, setLatestJob] = useState(null);
  // Newest-first list of past successful snapshots (jobId, snapshotTakenAt,
  // totalSizeBytes, tableCount, tables[]) — drives the history timeline and the
  // current-vs-previous table/column diff. Relational sources only.
  const [snapshotHistory, setSnapshotHistory] = useState([]);
  const [files, setFiles] = useState([]);
  const [filesTruncated, setFilesTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // folder path -> collapsed flag. Missing key = expanded (panels start open so the
  // files are visible without a click).
  const [collapsedGroups, setCollapsedGroups] = useState({});
  // File view: name/path search + file-type filter (empty set = all types).
  const [fileQuery, setFileQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState(() => new Set());
  // Database view: table-name search + column sort.
  const [tableQuery, setTableQuery] = useState('');
  const [tableSort, setTableSort] = useState({ key: 'name', dir: 'asc' });
  // Which history-timeline snapshot the page is showing. null = the latest (live
  // summary). Set by clicking a bar; cleared by the banner's "Back to latest".
  const [selectedJobId, setSelectedJobId] = useState(null);
  const [runProfileOpen, setRunProfileOpen] = useState(false);
  const [addToLibraryOpen, setAddToLibraryOpen] = useState(false);
  const [rerunning, setRerunning] = useState(false);
  const { setNotification, isReadOnly } = useContext(AppContext);
  const pollRef = useRef(null);
  const mountedRef = useRef(true);
  // jobId -> normalized snapshot. Caches getResults across polls so an in-flight run
  // being polled every 5s never refetches the already-loaded successful runs.
  const snapshotCacheRef = useRef(new Map());

  // Single loader: latest profile job (for status + a completed run to pull the per-file
  // list from) plus the per-source summary (latest snapshot's whole-folder counters). The
  // summary 404s when the source has never been profiled — treated as "not profiled yet",
  // not an error.
  const load = useCallback(async () => {
    let jobs = [];
    try {
      const [page, dto] = await Promise.all([
        listProfileJobs({ dataSourceId }),
        getDataSourceById(dataSourceId).catch(() => null),
      ]);
      jobs = (page && page.content) || (Array.isArray(page) ? page : []);
      if (mountedRef.current && dto) setSource(toUiDataSource(dto));
    } catch (e) {
      // non-fatal — summary may still resolve
    }
    if (!mountedRef.current) return;
    const latest = jobs[0] || null;
    setLatestJob(latest);

    let sum = null;
    try {
      sum = await getDataSourceSummary(dataSourceId);
    } catch (e) {
      // 404 = never profiled; surface a real error only when there's also no job to explain it
      if (!latest) {
        // leave summary null; render falls through to the empty state
      }
    }
    if (!mountedRef.current) return;
    setSummary(sum);
    setError(null);

    // File source with a completed run → pull the per-file listing from that run's results.
    if (sum && sum.fileScan) {
      const succeeded = jobs.find((j) => j.status === 'SUCCEEDED');
      if (succeeded) {
        try {
          const res = await getResults(succeeded.id);
          const payload = res && res.payload ? res.payload : res;
          const fs = payload && payload.fileScan ? payload.fileScan : null;
          if (mountedRef.current && fs) {
            setFiles(Array.isArray(fs.files) ? fs.files : []);
            setFilesTruncated(!!fs.filesTruncatedInPayload || !!fs.truncated);
          }
        } catch (e) {
          // list is best-effort; the header counters still render from the summary
        }
      }
    } else {
      setFiles([]);
      setFilesTruncated(false);

      // Relational source: build the snapshot history (newest first) from the most
      // recent successful runs. Per-run size lives only inside each run's results, so
      // fan out getResults — cached by jobId so polls don't refetch — capped to bound it.
      const succeeded = jobs.filter((j) => j.status === 'SUCCEEDED').slice(0, HISTORY_CAP);
      const snaps = await Promise.all(
        succeeded.map(async (j) => {
          const cache = snapshotCacheRef.current;
          if (cache.has(j.id)) return cache.get(j.id);
          try {
            const snap = normalizeSnapshot(j.id, await getResults(j.id));
            if (snap) cache.set(j.id, snap);
            return snap;
          } catch (e) {
            return null; // best-effort — a run whose results won't load just drops out
          }
        })
      );
      if (mountedRef.current) setSnapshotHistory(snaps.filter(Boolean));
    }
  }, [dataSourceId]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setSelectedJobId(null);
    load().finally(() => {
      if (mountedRef.current) setLoading(false);
    });
  }, [load]);

  // Poll while the latest job is in flight so an in-progress profile flips to results
  // without a manual refresh. Stops as soon as it's no longer active.
  useEffect(() => {
    const active = latestJob && ACTIVE_STATUSES.has(latestJob.status);
    if (active && !pollRef.current) {
      pollRef.current = setInterval(() => { load(); }, POLL_INTERVAL_MS);
    }
    if (!active && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [latestJob, load]);

  const isFileSource = !!(summary && summary.fileScan);
  // A Salesforce org: the platform row says SAAS / salesforce, or (when that row failed to load)
  // the snapshot's engine does. Same two signals as datasourcesApi.isSaasSource, inlined so the
  // page's existing test mocks of that module stay valid.
  const isSalesforce = !isFileSource && (
    (source && (source.datasourceType === 'SAAS' || String(source.type || '').toLowerCase() === 'salesforce'))
    || String((summary && summary.engine) || '').toLowerCase() === 'salesforce'
  );
  const jobActive = latestJob && ACTIVE_STATUSES.has(latestJob.status);

  // A file's effective type: the content-sniffed type when the profiler detected one,
  // else the extension-derived type. Trust-the-sniffer — the filter and counts key off
  // what the file actually is, not what its name claims. displaySniffType folds the
  // sniffer's prose fallback (DOCX on an unrecognized extension) into a generic TEXT.
  const effectiveType = (f) =>
    (f.sniffedType && displaySniffType(f.type, f.sniffedType)) || f.type || 'UNKNOWN';
  // Mismatch = the extension CONTRADICTS the content. A declared UNKNOWN (no
  // extension) isn't a contradiction — the sniff is an identification there.
  const isTypeMismatch = (f) =>
    !!(f.sniffedType && f.type && f.type !== 'UNKNOWN' && f.sniffedType !== f.type);

  // Distinct file types present, with counts — drives the type-filter chip strip.
  const fileTypes = useMemo(() => {
    const counts = new Map();
    files.forEach((f) => {
      const t = effectiveType(f);
      counts.set(t, (counts.get(t) || 0) + 1);
    });
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [files]);

  // Search (name/path, case-insensitive substring) + type filter, applied before
  // folder grouping so empty folders drop out of the panel list entirely.
  const filteredFiles = useMemo(() => {
    const q = fileQuery.trim().toLowerCase();
    return files.filter((f) => {
      if (typeFilter.size > 0 && !typeFilter.has(effectiveType(f))) return false;
      if (q && !String(f.path || f.name || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [files, fileQuery, typeFilter]);

  // Group the (filtered) flat file listing by the folder each file lives directly
  // under. One collapsible panel per folder; files-only, no nested-folder rows.
  // Groups and the files inside them are sorted by path so the layout is stable
  // across polls.
  const fileGroups = useMemo(() => {
    const map = new Map();
    filteredFiles.forEach((f) => {
      const dir = parentDir(f.path || f.name) || '/';
      if (!map.has(dir)) map.set(dir, []);
      map.get(dir).push(f);
    });
    return [...map.entries()]
      .map(([path, items]) => ({
        path,
        items: [...items].sort((a, b) => baseName(a).localeCompare(baseName(b))),
        totalBytes: items.reduce((s, f) => s + (f.sizeBytes || 0), 0),
      }))
      .sort((a, b) => a.path.localeCompare(b.path));
  }, [filteredFiles]);

  const toggleTypeFilter = (type) => {
    setTypeFilter((prev) => {
      const next = new Set(prev);
      if (next.has(type)) next.delete(type);
      else next.add(type);
      return next;
    });
  };

  // Which snapshot the relational view renders. Index 0 (the newest) is backed by the
  // live summary — richer per-table stats — so only indexes > 0 flip to historical mode.
  // A selection that's no longer in the history list (e.g. a fresh run pushed it past
  // the cap) silently falls back to latest.
  const selectedIndex = useMemo(
    () => (selectedJobId == null ? -1 : snapshotHistory.findIndex((s) => s.jobId === selectedJobId)),
    [selectedJobId, snapshotHistory]
  );
  const viewIndex = selectedIndex > 0 ? selectedIndex : 0;
  const isHistorical = viewIndex > 0;
  const viewSnapshot = snapshotHistory[viewIndex] || null;

  // Clicking the newest bar (or one already viewed) returns to the live summary.
  const handleSelectBar = (jobId) => {
    const newest = snapshotHistory[0] && snapshotHistory[0].jobId;
    setSelectedJobId(jobId === newest || jobId === selectedJobId ? null : jobId);
  };

  // Database view: table-name search + column sort over the snapshot's tables. In
  // historical mode the rows come from the selected run's results payload — same field
  // names, but no index/PK/FK/protection stats (the payload doesn't carry them).
  const sortedTables = useMemo(() => {
    const all = isHistorical
      ? (viewSnapshot ? viewSnapshot.tables : [])
      : (summary && summary.tables) || [];
    const q = tableQuery.trim().toLowerCase();
    const rows = q
      ? all.filter((t) => (String(t.tableName || '') + ' ' + String(t.schemaName || '')).toLowerCase().includes(q))
      : [...all];
    const { key, dir } = tableSort;
    const mult = dir === 'asc' ? 1 : -1;
    const num = (v) => (v == null || !Number.isFinite(Number(v)) ? -1 : Number(v));
    rows.sort((a, b) => {
      switch (key) {
        case 'schema': return String(a.schemaName || '').localeCompare(String(b.schemaName || '')) * mult;
        case 'columns': return (num(a.columnCount) - num(b.columnCount)) * mult;
        case 'rows': return (num(a.rowCount) - num(b.rowCount)) * mult;
        case 'size': return (num(a.sizeBytes) - num(b.sizeBytes)) * mult;
        case 'indexes': return (num(a.indexCount) - num(b.indexCount)) * mult;
        case 'pk': return ((a.hasPrimaryKey ? 1 : 0) - (b.hasPrimaryKey ? 1 : 0)) * mult;
        case 'fks': return (num(a.foreignKeyCount) - num(b.foreignKeyCount)) * mult;
        case 'uniques': return (num(a.uniqueConstraintCount) - num(b.uniqueConstraintCount)) * mult;
        case 'protection':
          return String(a.protectionOutcome || '').localeCompare(String(b.protectionOutcome || '')) * mult;
        default:
          return String(a.tableName || '').localeCompare(String(b.tableName || '')) * mult;
      }
    });
    return rows;
  }, [summary, tableQuery, tableSort, isHistorical, viewSnapshot]);

  // history[viewIndex] is the snapshot the page is showing; the entry after it is the
  // one before it in time — the "previous profile" the size column and the column diff
  // compare against. Follows the selection, so a historical view diffs against the
  // snapshot that preceded IT, not the latest run's predecessor.
  const prevSnapshot = snapshotHistory.length > viewIndex + 1 ? snapshotHistory[viewIndex + 1] : null;
  const hasPrev = !!prevSnapshot;

  // tableName -> prior-snapshot sizeBytes, for the "Prev size" column.
  const prevSizeByTable = useMemo(() => {
    const m = new Map();
    if (prevSnapshot) prevSnapshot.tables.forEach((t) => m.set(t.tableName, t.sizeBytes));
    return m;
  }, [prevSnapshot]);

  const toggleTableSort = (key) => {
    setTableSort((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  };

  // Columns for one table in the viewed / previous snapshot ([{name, dataType}] or null)
  // — handed to the table detail page as router state so it paints instantly. Both track
  // the timeline selection, so opening a table from a historical view shows that
  // snapshot's columns diffed against its own predecessor.
  const tableColumns = (snapshot, tableName) => columnsForTable(snapshot, tableName);

  // Navigate to the per-table detail page, passing the viewed + previous column lists so
  // the column/type diff renders without a refetch. Note: a plain reload of that page
  // re-derives from the LATEST run, so only the live view survives a refresh 1:1.
  const openTable = (tableName) => {
    history.push(
      `/metadata/source/${encodeURIComponent(dataSourceId)}/table/${encodeURIComponent(tableName)}`,
      {
        current: tableColumns(viewSnapshot || snapshotHistory[0], tableName) || [],
        previous: prevSnapshot ? (tableColumns(prevSnapshot, tableName) || []) : null,
        sourceName: (source && source.name) || (summary && summary.connection && summary.connection.name) || null,
      }
    );
  };

  // "Prev size" cell: the table's size at the previous snapshot, with a grew/shrank
  // arrow vs the current size. A table absent from the previous snapshot is "new".
  const renderPrevSizeCell = (t) => {
    if (!prevSizeByTable.has(t.tableName)) {
      return (
        <span style={{ color: STATUS.success.text, fontWeight: 600 }} title='Table did not exist at the previous fingerprint'>
          new
        </span>
      );
    }
    const prev = prevSizeByTable.get(t.tableName);
    const cur = t.sizeBytes;
    const bothNums = Number.isFinite(Number(prev)) && Number.isFinite(Number(cur));
    let delta = null;
    if (bothNums && Number(cur) !== Number(prev)) {
      const grew = Number(cur) > Number(prev);
      delta = (
        <span style={{ marginLeft: 6, color: grew ? STATUS.warning.text : STATUS.success.text }}
          title={grew ? 'Larger than the previous fingerprint' : 'Smaller than the previous fingerprint'}>
          {grew ? '▲' : '▼'}
        </span>
      );
    }
    return <span style={{ color: INK[500] }}>{bytesPretty(prev)}{delta}</span>;
  };

  const tableSortArrow = (key) =>
    tableSort.key === key ? (tableSort.dir === 'asc' ? ' ▲' : ' ▼') : '';

  const toggleGroup = (path) => {
    setCollapsedGroups((prev) => ({ ...prev, [path]: !prev[path] }));
  };

  // Clearing the map returns every group to its default (expanded); collapsing pins
  // each known group path to true.
  const expandAllGroups = () => setCollapsedGroups({});
  const collapseAllGroups = () =>
    setCollapsedGroups(fileGroups.reduce((acc, g) => ({ ...acc, [g.path]: true }), {}));

  const renderFileView = () => {
    const fs = summary.fileScan;
    const totalBytes = fs.totalBytes != null
      ? fs.totalBytes
      : files.reduce((s, f) => s + (f.sizeBytes || 0), 0);
    // Header: the saved source's name and root path. The snapshot's connection block
    // rarely carries either for file sources, so it's a fallback only.
    const sourceName = (source && source.name)
      || (summary.connection && summary.connection.name)
      || `Data source ${summary.dataSourceId}`;
    const sourcePath = (source && source.rootPath)
      || (summary.connection && summary.connection.database)
      || null;

    return (
      <>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 500 }}>
          {sourceName}
          <AdoptedFromBadge
            registeredServiceId={source && source.registeredServiceId}
            registeredServiceName={source && source.registeredServiceName}
          />
        </h2>
        {sourcePath && (
          <div style={{ fontFamily: 'monospace', fontSize: 13, color: INK[400], marginTop: 2 }}>
            {sourcePath}
          </div>
        )}

        {/* Total size at the top, per requirement, alongside the whole-folder counters. */}
        <div style={{
          marginTop: 16, display: 'flex', gap: 40, flexWrap: 'wrap',
          padding: '16px 20px', border: `1px solid ${INK[150]}`, borderRadius: RADIUS.md,
          background: '#fff', boxShadow: ELEVATION.e1,
        }}>
          <Stat label='Total size' value={formatBytes(totalBytes)} accent={STATUS.success.text} />
          <Stat label='Files' value={rowsPretty(fs.totalFiles)} />
          <Stat label='Analyzed' value={rowsPretty(fs.analyzedFiles)} />
          <Stat label='Unanalyzed' value={rowsPretty(fs.unanalyzedFiles)} />
          <Stat label='Skipped' value={rowsPretty(fs.skippedCount)} />
          {fs.typeMismatches > 0 && (
            <Stat label='Type mismatches' value={rowsPretty(fs.typeMismatches)} accent={STATUS.warning.text} />
          )}
        </div>

        <div style={{ marginTop: 12, display: 'flex', gap: 12, fontSize: 12, color: INK[500] }}>
          <span>Snapshot taken {formatInstant(summary.snapshotTakenAt)}</span>
        </div>

        <div style={{ margin: '24px 0 8px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <h3 style={{ margin: 0, fontSize: 12, fontWeight: 600, color: INK[400], textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Files ({rowsPretty(fs.totalFiles)})
          </h3>
          <input
            type='text'
            data-testid='file-search'
            placeholder='Search files…'
            value={fileQuery}
            onChange={(e) => setFileQuery(e.target.value)}
            style={searchInputStyle}
          />
          {(fileQuery.trim() !== '' || typeFilter.size > 0) && (
            <span style={{ fontSize: 12, color: INK[500] }}>
              {filteredFiles.length} of {files.length}{filesTruncated ? ' loaded' : ''} files shown
            </span>
          )}
          {fileGroups.length > 0 && (
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <button type='button' style={groupBtnStyle} onClick={expandAllGroups}>
                Expand all
              </button>
              <button type='button' style={groupBtnStyle} onClick={collapseAllGroups}>
                Collapse all
              </button>
            </div>
          )}
        </div>
        {fileTypes.length > 1 && (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 600, color: INK[400], textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Type
            </span>
            {fileTypes.map(([type, count]) => {
              const active = typeFilter.has(type);
              return (
                <button
                  key={type}
                  type='button'
                  data-testid={`file-type-filter-${type}`}
                  onClick={() => toggleTypeFilter(type)}
                  title={active ? 'Click to stop filtering by this type' : `Show only ${type} files`}
                  style={{
                    ...groupBtnStyle,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    padding: '3px 8px',
                    opacity: typeFilter.size === 0 || active ? 1 : 0.45,
                    borderColor: active ? BRAND.blue : INK[200],
                    background: active ? STATUS.info.bg : '#fff',
                  }}
                >
                  <FileTypeChip type={type} />
                  <span>{count}</span>
                </button>
              );
            })}
            {typeFilter.size > 0 && (
              <button
                type='button'
                style={{ ...groupBtnStyle, border: 'none' }}
                onClick={() => setTypeFilter(new Set())}
              >
                Clear
              </button>
            )}
          </div>
        )}
        {filesTruncated && (
          <div style={{ fontSize: 12, color: INK[500], marginBottom: 6 }}>
            File list capped for display — showing the first {rowsPretty(files.length)} of{' '}
            {rowsPretty(fs.totalFiles)} files. Search and type filters cover only these; the
            totals above are still exact.
          </div>
        )}
        {fileGroups.length === 0 ? (
          <div className='metadata-empty' style={{ padding: 24 }}>
            {files.length > 0
              ? 'No files match the current search / type filter.'
              : 'No per-file listing available for this fingerprint run.'}
          </div>
        ) : (
          <div style={{ border: `1px solid ${INK[150]}`, borderRadius: RADIUS.md, overflow: 'hidden' }}>
            {fileGroups.map((g, gi) => {
              const isOpen = collapsedGroups[g.path] !== true;
              return (
                <div key={g.path} style={{ borderTop: gi === 0 ? 'none' : `1px solid ${INK[100]}` }}>
                  <div
                    onClick={() => toggleGroup(g.path)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px',
                      cursor: 'pointer', textAlign: 'left', background: INK[50],
                    }}
                  >
                    {isOpen ? <BsChevronDown size={13} /> : <BsChevronRight size={13} />}
                    <GoFileDirectory style={{ flexShrink: 0, opacity: 0.7 }} />
                    <span style={{ fontWeight: 600, fontSize: 13, color: INK[500], whiteSpace: 'nowrap' }}>
                      {g.items.length} {g.items.length === 1 ? 'file' : 'files'}
                    </span>
                    {/* Folder drill-down link — row click still expands/collapses. The
                        synthetic '/' group (files with no slash in their path) has no
                        addressable URL segment, so it renders as plain text. */}
                    {encodeFolderPathForUrl(g.path) ? (
                      <span
                        role='link'
                        tabIndex={0}
                        data-testid='folder-detail-link'
                        title='Open folder detail — discover this folder or a subset of its files'
                        onClick={(e) => {
                          e.stopPropagation();
                          history.push(
                            `/metadata/source/${encodeURIComponent(dataSourceId)}/folder/${encodeFolderPathForUrl(g.path)}`,
                            { files: g.items, folderPath: g.path }
                          );
                        }}
                        onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.click(); }}
                        style={{
                          fontFamily: 'monospace', fontSize: 13, fontWeight: 600,
                          color: BRAND.blue, cursor: 'pointer', textDecoration: 'underline',
                          textDecorationColor: 'rgba(49,93,255,0.35)',
                        }}
                      >
                        {g.path}
                      </span>
                    ) : (
                      <span style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, color: INK[900] }}>
                        {g.path}
                      </span>
                    )}
                    <span style={{ fontSize: 12, color: INK[500], marginLeft: 'auto', whiteSpace: 'nowrap' }}>
                      {formatBytes(g.totalBytes)}
                    </span>
                  </div>
                  {isOpen && (
                    <div style={{ overflow: 'auto' }}>
                      <table className='metadata-itbl'>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', paddingLeft: 10 }}>File</th>
                            <th style={{ textAlign: 'left', paddingLeft: 10 }}>Type</th>
                            <th style={{ textAlign: 'left', paddingLeft: 10 }}>Detected</th>
                            <th>Size</th>
                            <th>Analyzed</th>
                          </tr>
                        </thead>
                        <tbody>
                          {g.items.map((f, i) => (
                            <tr key={f.path || `${f.name}-${i}`}>
                              <td style={{ fontFamily: 'monospace', fontSize: 12, textAlign: 'left', paddingLeft: 10 }}>
                                {baseName(f)}
                                {f.notice && (
                                  <div style={{ fontSize: 11, color: STATUS.danger.text }}>{f.notice}</div>
                                )}
                              </td>
                              <td style={{ textAlign: 'left', paddingLeft: 10 }}>{f.type ? <FileTypeChip type={f.type} /> : '—'}</td>
                              <td style={{ textAlign: 'left', paddingLeft: 10 }}>
                                {f.sniffedType ? (
                                  <span
                                    title={isTypeMismatch(f)
                                      ? `Content is ${f.sniffedType} but the file name says ${f.type} — the detected type is used for analysis`
                                      : displaySniffType(f.type, f.sniffedType) === 'TEXT'
                                        ? 'File type not recognized from the name — content is readable text'
                                        : (!f.type || f.type === 'UNKNOWN')
                                          ? 'File type not recognized from the name — identified from content'
                                          : 'Content matches the file extension'}
                                    style={isTypeMismatch(f) ? {
                                      display: 'inline-flex', alignItems: 'center', gap: 4,
                                      padding: '1px 4px', borderRadius: 4,
                                      border: '1px solid rgba(180,83,9,0.45)', background: 'rgba(180,83,9,0.06)',
                                    } : undefined}
                                  >
                                    {isTypeMismatch(f) && <span style={{ fontSize: 11, color: STATUS.warning.text }}>⚠</span>}
                                    <FileTypeChip type={displaySniffType(f.type, f.sniffedType)} />
                                  </span>
                                ) : '—'}
                              </td>
                              <td style={{ color: INK[500] }}>{formatBytes(f.sizeBytes)}</td>
                              <td style={{ color: INK[500] }}>{f.analyzed ? '✓' : '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </>
    );
  };

  // Title comes from the saved data source record — the worker's snapshot connection
  // block never carries a name (host/port/database/schema only), so without the
  // platform record every relational page was titled "Data source N".
  // Header counters follow the timeline selection. The historical payload has no
  // totalRowCount; sum the per-table counts when any are present, else show '—'.
  const viewStats = () => {
    const viewTableCount = isHistorical ? viewSnapshot.tableCount : summary.tableCount;
    const viewTotalSize = isHistorical ? viewSnapshot.totalSizeBytes : summary.totalSizeBytes;
    const viewUserCount = isHistorical
      ? (viewSnapshot.userCount != null ? viewSnapshot.userCount : '—')
      : summary.userCount;
    const viewSnapshotTakenAt = isHistorical ? viewSnapshot.snapshotTakenAt : summary.snapshotTakenAt;
    const viewRowCount = isHistorical
      ? (viewSnapshot.tables.some((t) => t.rowCount != null)
        ? viewSnapshot.tables.reduce((s, t) => s + (t.rowCount || 0), 0)
        : null)
      : summary.totalRowCount;
    // Empty tables/collections in this snapshot — a confirmed zero row/doc count (rowCount === 0;
    // null means "not measured", not empty, so it's excluded). These are the ones a discovery run
    // skips; profiling still records them, so surface the count here. Over the full view set, not
    // the search-filtered sortedTables, so it lines up with viewTableCount.
    const viewTablesAll = (isHistorical ? (viewSnapshot ? viewSnapshot.tables : []) : (summary && summary.tables)) || [];
    const viewEmptyTableCount = viewTablesAll.filter((t) => t.rowCount === 0).length;
    return { viewTableCount, viewTotalSize, viewUserCount, viewSnapshotTakenAt, viewRowCount, viewEmptyTableCount };
  };

  // Header shared by the relational and Salesforce views: title, engine line, two fact
  // columns, the counts strip, the profile-history timeline and the historical banner.
  // `showUsers` is off for Salesforce — its collector surfaces no users.
  const renderSourceHeader = ({ nouns, engineFacts, connectionFacts, showUsers, totalLabel = 'total', extraStats = null }) => {
    const v = viewStats();
    const sectionStyle = { margin: '0 0 6px', fontSize: 12, fontWeight: 600, color: INK[400], textTransform: 'uppercase', letterSpacing: '0.06em' };
    return (
    <>
      <h2 style={{ margin: 0, fontSize: 18, fontWeight: 500 }}>
        {(source && source.name)
          || (summary.connection && summary.connection.name)
          || `Data source ${summary.dataSourceId}`}
        <AdoptedFromBadge registeredServiceId={source && source.registeredServiceId} />
      </h2>
      <div style={{ fontStyle: 'italic', fontSize: 13, color: INK[400], marginTop: 2 }}>
        {[
          summary.engine,
          summary.connection && summary.connection.database,
          summary.connection && summary.connection.schema,
        ].filter(Boolean).join(' · ')}
      </div>
      <div style={{ marginTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>
        <section>
          <h3 style={sectionStyle}>Engine</h3>
          {engineFacts.map(([label, value]) => <FactRow key={label} label={label} value={value} />)}
        </section>
        <section>
          <h3 style={sectionStyle}>Connection</h3>
          {connectionFacts.map(([label, value]) => <FactRow key={label} label={label} value={value} />)}
          <FactRow label='Snapshot taken' value={formatInstant(v.viewSnapshotTakenAt)} />
        </section>
      </div>

      <div style={{ marginTop: 24, display: 'flex', gap: 24, fontSize: 12, color: INK[500] }}>
        <span><strong>{v.viewTableCount}</strong> {nouns.item}</span>
        {v.viewEmptyTableCount > 0 && (
          <span><strong>{v.viewEmptyTableCount}</strong> empty</span>
        )}
        <span><strong>{rowsPretty(v.viewRowCount)}</strong> {nouns.record}</span>
        <span><strong>{bytesPretty(v.viewTotalSize)}</strong> {totalLabel}</span>
        {extraStats}
        {showUsers && <span><strong>{v.viewUserCount}</strong> users</span>}
      </div>

      <ProfileHistoryTimeline
        history={snapshotHistory}
        formatBytes={bytesPretty}
        formatInstant={formatInstant}
        selectedJobId={isHistorical ? viewSnapshot.jobId : null}
        onSelectBar={handleSelectBar}
      />

      {isHistorical && (
        <div
          data-testid='historical-snapshot-banner'
          style={{
            marginTop: 16, padding: '10px 14px', display: 'flex', alignItems: 'center',
            gap: 12, flexWrap: 'wrap', borderRadius: RADIUS.md,
            border: `1px solid ${BRAND.blue}`, background: STATUS.info.bg,
            fontSize: 13, color: INK[900],
          }}
        >
          <span>
            Viewing the fingerprint taken <strong>{formatInstant(viewSnapshot.snapshotTakenAt)}</strong>
            {' '}— index, key, and protection details are only available on the latest fingerprint.
          </span>
          <button
            type='button'
            data-testid='back-to-latest'
            style={{ ...groupBtnStyle, borderColor: BRAND.blue, color: BRAND.blue, marginLeft: 'auto' }}
            onClick={() => setSelectedJobId(null)}
          >
            Back to latest
          </button>
        </div>
      )}
    </>
    );
  };

  // Salesforce (SAAS) latest view: the object list with record counts + storage estimate and
  // the per-object network, both off the Data Graph the Describe run built. Historical
  // timeline picks fall back to the snapshot table list (the graph is latest-only).
  const renderSalesforceView = () => {
    const nouns = getSourceNomenclature(summary.engine, 'SAAS');
    // The org's REAL data storage (from /limits, on the snapshot since discover V38) beside the
    // estimated sum — the only way to see how far the per-object estimates are off for this org.
    const attrs = (summary.attributes && typeof summary.attributes === 'object') ? summary.attributes : {};
    const orgUsed = attrs.orgDataStorageUsedBytes != null ? Number(attrs.orgDataStorageUsedBytes) : null;
    const orgMax = attrs.orgDataStorageMaxBytes != null ? Number(attrs.orgDataStorageMaxBytes) : null;
    const orgPct = orgUsed != null && orgMax > 0 ? Math.round((orgUsed / orgMax) * 100) : null;
    const orgStorage = orgUsed != null ? (
      <span data-testid='sf-org-storage' title='Data storage the org reports in /limits — the estimate above should land near this'>
        <strong>{bytesPretty(orgUsed)}</strong> actually used{orgMax != null ? ` of ${bytesPretty(orgMax)}` : ''}{orgPct != null ? ` (${orgPct}%)` : ''}
      </span>
    ) : null;
    return (
    <>
      {renderSourceHeader({
        nouns,
        engineFacts: [['Engine', summary.engine], ['Version', summary.engineVersion]],
        connectionFacts: [
          ['Org', summary.schemaNames && summary.schemaNames.length > 0 ? summary.schemaNames.join(', ') : null],
          ['Login URL', source && source.loginUrl ? source.loginUrl : null],
        ],
        showUsers: false,
        totalLabel: 'estimated',
        extraStats: orgStorage,
      })}
      <SalesforceObjectsView
        dataSourceId={dataSourceId}
        nouns={nouns}
        jobActive={Boolean(jobActive)}
        describing={rerunning}
        onDescribe={handleRerun}
        onOpenObject={(table) => history.push(
          `/metadata/source/${encodeURIComponent(dataSourceId)}/object/${encodeURIComponent(table)}`)}
        // the owner's Discover choice (config.selectedObjects) is edited here since 2026-11-04 — a data
        // graph's ticks are that graph's own object list
        selectable
        source={source}
        onSelectionSaved={(list) => setSource((prev) => ({ ...(prev || {}), selectedObjects: list }))}
      />
    </>
    );
  };

  const renderRelationalView = () => {
    // Relational view also serves NoSQL (file sources render renderFileView instead); label
    // the item/record nouns per engine — "collections" for mongo, "tables" otherwise.
    const relationalNouns = getSourceNomenclature(summary.engine);
    const relationalItemCap = relationalNouns.item.charAt(0).toUpperCase() + relationalNouns.item.slice(1);
    const viewUsers = (isHistorical ? viewSnapshot.users : summary.users) || [];

    return (
    <>
      {renderSourceHeader({
        nouns: relationalNouns,
        engineFacts: [
          ['Engine', summary.engine],
          ['Version', summary.engineVersion],
          ['Character set', summary.characterSet],
          ['Collation', summary.collation],
          ['Schemas', summary.schemaNames && summary.schemaNames.length > 0 ? summary.schemaNames.join(', ') : null],
        ],
        connectionFacts: [
          ['Host', summary.connection && summary.connection.host],
          ['Port', summary.connection && summary.connection.port != null ? String(summary.connection.port) : null],
          ['Database', summary.connection && summary.connection.database],
          ['Schema', summary.connection && summary.connection.schema],
        ],
        showUsers: true,
      })}

      <div style={{ margin: '24px 0 8px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: 12, fontWeight: 600, color: INK[400], textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {relationalItemCap} ({isHistorical ? viewSnapshot.tables.length : (summary.tables || []).length})
        </h3>
        <input
          type='text'
          data-testid='table-search'
          placeholder={`Search ${relationalNouns.item}…`}
          value={tableQuery}
          onChange={(e) => setTableQuery(e.target.value)}
          style={searchInputStyle}
        />
        {tableQuery.trim() !== '' && (
          <span style={{ fontSize: 12, color: INK[500] }}>
            {sortedTables.length} of {isHistorical ? viewSnapshot.tables.length : (summary.tables || []).length} {relationalNouns.item} shown
          </span>
        )}
      </div>
      {/* No overflow here — it would trap the sticky header inside this box instead of the page. */}
      <div style={{ border: `1px solid ${INK[150]}`, borderRadius: RADIUS.md }}>
        <table className='metadata-itbl'>
          <thead className='metadata-sticky-head metadata-sticky-head--padded'>
            <tr>
              {[
                ['name', 'Table', true],
                // Schema column (C2D-1116/1093): distinguishes same-named tables in different
                // schemas (Oracle multi-owner, multi-schema Postgres/SQL Server) that otherwise
                // rendered as indistinguishable duplicate rows.
                ['schema', 'Schema', true],
                ['columns', 'Columns', true],
                ['rows', 'Rows', true],
                ['size', 'Size', true],
                ...(hasPrev ? [['prevSize', 'Prev size', false]] : []),
                // Index/key/protection stats live only in the latest-snapshot summary —
                // historical results payloads don't carry them, so drop the columns
                // rather than render a wall of dashes.
                ...(isHistorical ? [] : [
                  ['indexes', 'Indexes', true],
                  ['pk', 'PK', true],
                  ['fks', 'FKs', true],
                  ['uniques', 'Uniques', true],
                  ['protection', 'Protection', true],
                ]),
              ].map(([key, label, sortable]) => (
                <th
                  key={key}
                  onClick={sortable ? () => toggleTableSort(key) : undefined}
                  style={{ cursor: sortable ? 'pointer' : 'default', textAlign: 'left', paddingLeft: 10, userSelect: 'none' }}
                  title={sortable ? `Sort by ${label.toLowerCase()}` : 'Size at the previous fingerprint'}
                >
                  {label}{sortable ? tableSortArrow(key) : ''}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedTables.map((t) => (
              <tr key={t.id || `${t.schemaName || ''}.${t.tableName}`}>
                <td style={{ fontFamily: 'monospace', fontSize: 12, textAlign: 'left', paddingLeft: 10 }}>
                  <span
                    role='link'
                    tabIndex={0}
                    data-testid={`table-open-${t.tableName}`}
                    onClick={() => openTable(t.tableName)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); openTable(t.tableName); } }}
                    title='Open table detail — columns, types, and changes since the previous fingerprint'
                    style={{
                      cursor: 'pointer', color: BRAND.blue, textDecoration: 'underline',
                      textDecorationColor: 'rgba(49,93,255,0.35)',
                    }}
                  >
                    {t.tableName}
                  </span>
                  {t.notice && (
                    <div style={{ fontSize: 11, color: STATUS.danger.text }}>{t.notice}</div>
                  )}
                </td>
                <td style={{ fontFamily: 'monospace', color: INK[500], fontSize: 12, textAlign: 'left', paddingLeft: 10 }}>{t.schemaName || '—'}</td>
                <td style={{ color: INK[500], fontSize: 12, textAlign: 'left', paddingLeft: 10 }}>{t.columnCount}</td>
                <td style={{ color: INK[500], fontSize: 12, textAlign: 'left', paddingLeft: 10 }}>{rowsPretty(t.rowCount)}</td>
                <td style={{ color: INK[500], fontSize: 12, textAlign: 'left', paddingLeft: 10 }}>{bytesPretty(t.sizeBytes)}</td>
                {hasPrev && (
                  <td style={{ fontSize: 12, textAlign: 'left', paddingLeft: 10 }}>
                    {renderPrevSizeCell(t)}
                  </td>
                )}
                {!isHistorical && (
                  <>
                    <td style={{ color: INK[500], fontSize: 12, textAlign: 'left', paddingLeft: 10 }}>{t.indexCount != null ? t.indexCount : '—'}</td>
                    <td style={{ color: INK[500], fontSize: 12, textAlign: 'left', paddingLeft: 10 }}>{t.hasPrimaryKey == null ? '—' : t.hasPrimaryKey ? '✓' : '—'}</td>
                    <td style={{ color: INK[500], fontSize: 12, textAlign: 'left', paddingLeft: 10 }}>{t.foreignKeyCount != null ? t.foreignKeyCount : '—'}</td>
                    <td style={{ color: INK[500], fontSize: 12, textAlign: 'left', paddingLeft: 10 }}>{t.uniqueConstraintCount != null ? t.uniqueConstraintCount : '—'}</td>
                    <td style={{ color: INK[500], fontSize: 12, textAlign: 'left', paddingLeft: 10 }}>{t.protectionOutcome || '—'}</td>
                  </>
                )}
              </tr>
            ))}
            {sortedTables.length === 0 && (
              <tr>
                <td
                  colSpan={(isHistorical ? 4 : 9) + (hasPrev ? 1 : 0)}
                  style={{ textAlign: 'left', paddingLeft: 10, color: INK[400] }}
                >
                  No {relationalNouns.item} match the current search.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h3 style={{ margin: '24px 0 8px', fontSize: 12, fontWeight: 600, color: INK[400], textTransform: 'uppercase', letterSpacing: '0.06em' }}>
        Users ({viewUsers.length})
      </h3>
      {viewUsers.length === 0 ? (
        <div className='metadata-empty' style={{ padding: 24 }}>
          No users surfaced by the connecting account.
        </div>
      ) : (
        <div style={{ overflow: 'auto', border: `1px solid ${INK[150]}`, borderRadius: RADIUS.md }}>
          <table className='metadata-itbl'>
            <thead>
              <tr>
                <th>Username</th>
                <th>Roles</th>
                <th>Last login</th>
              </tr>
            </thead>
            <tbody>
              {viewUsers.map((u) => (
                <tr key={u.username}>
                  <td>{u.username}</td>
                  <td style={{ color: INK[500] }}>{(u.roles && u.roles.length > 0) ? u.roles.join(', ') : '—'}</td>
                  <td style={{ color: INK[500] }}>{formatInstant(u.lastLogin)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
    );
  };

  const renderEmptyState = () => {
    if (jobActive) {
      return (
        <div className='metadata-empty' style={{ padding: 32 }}>
          Fingerprinting in progress… this view refreshes automatically when the run completes.
        </div>
      );
    }
    if (latestJob && latestJob.status === 'FAILED') {
      return (
        <div className='metadata-empty' style={{ padding: 32, color: STATUS.danger.text }}>
          The last fingerprint run failed{latestJob.errorCode ? ` (${latestJob.errorCode})` : ''}.
          Use Actions → Refresh metadata to re-run it.
        </div>
      );
    }
    return (
      <div className='metadata-empty' style={{ padding: 32 }}>
        Not fingerprinted yet. Use Actions → Refresh metadata to run one now.
      </div>
    );
  };

  // Actions → Refresh metadata: re-run the profile for THIS source. The in-page
  // poller flips the view to the new results when the run completes.
  const handleRerun = async () => {
    setRerunning(true);
    try {
      const gate = await ensureConnectionTested(Number(dataSourceId), {
        history,
        setNotification,
        name: source && source.name,
      });
      if (!gate.ok) return;
      const job = await startProfileJob(Number(dataSourceId));
      setNotification({
        header: 'Fingerprint started',
        body: (
          <span>
            Re-running fingerprint for {(source && source.name) || `data source ${dataSourceId}`}
            {job && job.id ? ` (job ${job.id})` : ''}.
          </span>
        ),
      });
      load();
    } catch (e) {
      setNotification({
        header: 'Fingerprint start failed',
        body: <span>{(e && e.message) || 'Could not start the fingerprint run.'}</span>,
      });
    } finally {
      if (mountedRef.current) setRerunning(false);
    }
  };

  return (
    <div className='metadata-page'>
      <div className='metadata-toolbar'>
        <Breadcrumbs
          className='mb-0'
          items={[
            { label: 'Schema Intelligence', to: '/metadata' },
            { label: (source && source.name) || (summary && summary.connection && summary.connection.name) || 'Data source' },
          ]}
        />
        <div style={{ marginLeft: 'auto' }}>
          <Dropdown alignRight>
            <Dropdown.Toggle
              data-testid='metadata-actions-toggle'
              size='sm'
              className='pr-4 pl-4'
              variant='outline-secondary'
            >
              Actions
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                data-testid='metadata-action-refresh'
                disabled={rerunning || jobActive || !!isReadOnly}
                onClick={handleRerun}
                title={isReadOnly ? READ_ONLY_TITLE : jobActive
                  ? 'A fingerprint run is already in progress for this source'
                  : (isSalesforce ? 'Re-run the Describe for this org' : 'Re-run the fingerprint for this data source')}
              >
                <BsArrowRepeat className='mr-2' />
                {isSalesforce ? 'Re-run Describe' : 'Refresh metadata'}
              </Dropdown.Item>
              <Dropdown.Item
                data-testid='metadata-action-new-profile'
                disabled={!!isReadOnly}
                title={isReadOnly ? READ_ONLY_TITLE : undefined}
                onClick={() => setRunProfileOpen(true)}
              >
                <BsPlus className='mr-2' />
                New fingerprint…
              </Dropdown.Item>
              {/* Promote to a fingerprint: admin-only, relational sources with a profiled
                  snapshot. File sources aren't fingerprint-matched, so it's hidden there. */}
              {isAdmin && !isFileSource && summary && (
                <Dropdown.Item
                  data-testid='metadata-action-add-to-library'
                  onClick={() => setAddToLibraryOpen(true)}
                  title='Save this database’s table signatures as a reusable schema fingerprint'
                >
                  <BsBookmarkPlus className='mr-2' />
                  Add to schema library…
                </Dropdown.Item>
              )}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, background: '#fff' }}>
        {loading && (
          <div>
            <Skeleton height={28} width={320} />
            <div style={{ marginTop: 12 }}><Skeleton height={120} /></div>
            <div style={{ marginTop: 12 }}><Skeleton height={240} /></div>
          </div>
        )}
        {error && !loading && (
          <div className='metadata-empty' style={{ color: STATUS.danger.text }}>{error}</div>
        )}
        {!loading && !error && summary && (isFileSource
          ? renderFileView()
          : (isSalesforce && !isHistorical ? renderSalesforceView() : renderRelationalView()))}
        {!loading && !error && !summary && renderEmptyState()}
      </div>
      <RunProfileModal
        show={runProfileOpen}
        onHide={() => setRunProfileOpen(false)}
        onSuccess={(job) => {
          setNotification({
            header: 'Fingerprint started',
            body: (
              <span>
                Fingerprint run started (job {job && job.id ? job.id : ''}). Results will appear when it completes.
              </span>
            ),
          });
          load();
        }}
      />
      <AddToSchemaLibraryDialog
        show={addToLibraryOpen}
        onHide={() => setAddToLibraryOpen(false)}
        summary={summary}
        sourceName={(source && source.name) || (summary && summary.connection && summary.connection.name)}
        onSuccess={(created) => {
          setNotification({
            header: 'Fingerprint added',
            body: (
              <span>
                {created && created.productName ? `${created.vendorName} ${created.productName}` : 'The fingerprint'}
                {' '}is now in the schema library. Future fingerprint runs will match against it.
              </span>
            ),
          });
        }}
      />
    </div>
  );
};

export default DataSourceDetail;
